import React from "react";

import { Autocomplete, Stack, TextField } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { CharAutocomplete } from "components/Characteristics/Deal-form-drawer/CharAutocomplete";
import { NumberInput } from "components/forms/Number-input/NumberInput";
import { currenciesArray } from "constants/index";
import dayjs from "dayjs";
import { useDateFormatSelect } from "store";
import { CharacteristicDef } from "store/types";

interface Props {
  characteristic: CharacteristicDef;
  value: string;
  onChange: (value: string) => void;
  currency?: string | null;
  onCurrencyChange?: (currency: string | null) => void;
  error?: boolean;
  helperText?: string;
}

export const CharacteristicField: React.FC<Props> = ({
  characteristic,
  value,
  onChange,
  currency,
  onCurrencyChange,
  error,
  helperText
}) => {
  const { name, data_type, required } = characteristic;
  const dateFormat = useDateFormatSelect();

  switch (data_type) {
    case "number":
      return (
        <NumberInput
          fullWidth
          label={name}
          value={value}
          required={required}
          error={error}
          helperText={helperText}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "percentage":
      return (
        <NumberInput
          fullWidth
          label={name}
          value={value}
          required={required}
          error={error}
          helperText={helperText}
          onChange={(e) => onChange(e.target.value)}
          numberInputProps={{ suffix: "%", decimalScale: 2 }}
        />
      );

    case "currency":
      return (
        <Stack direction="row" gap="10px" alignItems="flex-start">
          <NumberInput
            fullWidth
            label={name}
            value={value}
            required={required}
            error={error}
            helperText={helperText}
            onChange={(e) => onChange(e.target.value)}
            numberInputProps={{ thousandSeparator: true }}
          />
          <Autocomplete
            options={currenciesArray}
            value={currency || ""}
            onChange={(_, option) => onCurrencyChange?.(option)}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                label="Currency"
                sx={{ width: "110px", minWidth: "110px" }}
                required={required}
                error={error}
              />
            )}
          />
        </Stack>
      );

    case "date":
      return (
        <DesktopDatePicker
          format={dateFormat}
          views={["year", "month", "day"]}
          slotProps={{
            inputAdornment: { position: "start" },
            popper: {
              sx: {
                minWidth: "335px",
                ".MuiPickersCalendarHeader-labelContainer": { fontSize: "0.9rem" }
              }
            },
            textField: {
              sx: { padding: 0, svg: { color: "#0a3142" } },
              placeholder: "dd / mm / yyyy",
              fullWidth: true,
              required,
              error,
              helperText
            },
            field: {
              clearable: true,
              onClear: () => onChange("")
            }
          }}
          value={value ? dayjs(value) : null}
          label={name}
          onChange={(val) => onChange(val ? val.toISOString() : "")}
        />
      );

    default:
      return (
        <CharAutocomplete
          characteristicId={characteristic.id}
          charName={name}
          value={value}
          onChange={onChange}
          required={required}
          error={error}
          helperText={helperText}
        />
      );
  }
};
